// TrackFiji — GPS position quality (shared by the browser, netlify/functions/history.mts and — as a copy — supabase/functions/ingest)
//
// Problem: trackers parked indoors report "no fix" (valid=false) with stale or drifting coordinates, and after a long
// gap a tracker can report a position kilometres away with no record of getting there (e.g. ST-901, 11 Oct 2026).
//
// Rules:
//  * valid=false              → quality "no_fix": keep showing the last trusted position.
//  * weak fix (sat<4, hdop>5) → quality "low": shown, with a warning.
//  * a jump > JUMP_M from the last trusted position that is either too fast (> MAX_KMH) or comes after a
//    gap with no trusted fixes (> GAP_MS) → quality "suspect": held until confirmed by CONFIRM_N consistent fixes
//    spanning CONFIRM_SPAN_MS. A cluster that doesn't move while reporting speed (> 10 km/h) is a stuck fix and
//    never confirms.

export type Fix = { lat: number; lng: number; t: number; kmh: number; valid: boolean; sat?: number | null; hdop?: number | null };
export type Trusted = { lat: number; lng: number; t: number };
export type Pending = { pts: { lat: number; lng: number; t: number; kmh: number }[] };
export type Quality = "good" | "low" | "no_fix" | "suspect";
export type Verdict = { hold: boolean; quality: Quality; pending: Pending | null; confirmed?: Pending["pts"] };

export const JUMP_M = 800;
export const MAX_KMH = 160;
export const GAP_MS = 10 * 60_000;
export const CONFIRM_N = 3;
export const CONFIRM_SPAN_MS = 20_000;
export const STUCK_M = 25;

export function metres(aLat: number, aLng: number, bLat: number, bLng: number) {
  const r = Math.PI / 180, R = 6371000;
  const h = Math.sin((bLat - aLat) * r / 2) ** 2 + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin((bLng - aLng) * r / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
const kmhBetween = (a: { lat: number; lng: number; t: number }, b: { lat: number; lng: number; t: number }) => {
  const dt = b.t - a.t;
  return dt > 0 ? metres(a.lat, a.lng, b.lat, b.lng) / 1000 / (dt / 3_600_000) : Infinity;
};

export function assess(trusted: Trusted | null, pending: Pending | null, fix: Fix): Verdict {
  if (!fix.valid) return { hold: !!trusted, quality: "no_fix", pending };
  const weak = (fix.sat != null && fix.sat < 4) || (fix.hdop != null && fix.hdop > 5);
  const ok: Verdict = { hold: false, quality: weak ? "low" : "good", pending: null };
  if (!trusted) return ok;

  const d = metres(trusted.lat, trusted.lng, fix.lat, fix.lng);
  const dt = fix.t - trusted.t;
  const jump = d > JUMP_M && (kmhBetween(trusted, fix) > MAX_KMH || dt > GAP_MS);
  if (!jump) return ok;

  let pts = pending?.pts ?? [];
  const last = pts[pts.length - 1];
  if (last && (fix.t <= last.t || kmhBetween(last, fix) > MAX_KMH)) pts = [];
  pts = [...pts, { lat: fix.lat, lng: fix.lng, t: fix.t, kmh: fix.kmh }].slice(-12);

  const span = pts[pts.length - 1].t - pts[0].t;
  const spread = Math.max(...pts.map((p) => metres(pts[0].lat, pts[0].lng, p.lat, p.lng)));
  const avgKmh = pts.reduce((s, p) => s + p.kmh, 0) / pts.length;
  const stuck = pts.length >= CONFIRM_N && spread < STUCK_M && avgKmh > 10;
  if (pts.length >= CONFIRM_N && span >= CONFIRM_SPAN_MS && !stuck) return { ...ok, confirmed: pts };
  return { hold: true, quality: "suspect", pending: { pts } };
}

export const QUALITY_NOTE: Record<Quality, string> = {
  good: "",
  low: "Weak GPS signal — accuracy may be low.",
  no_fix: "No GPS fix (likely indoors or under a roof) — showing the last confirmed position.",
  suspect: "Unconfirmed position jump — showing the last confirmed position until the new one is verified.",
};
